import type { Pool, PoolStatus } from "../shared/types";
import { effectiveStatus } from "./util";

export interface Filters {
  /** Show only pools with an open section right now. */
  openNow: boolean;
  /** Restrict to indoor-only, outdoor-only, or all pools. */
  indoor: "all" | "indoor" | "outdoor";
  /** Show only pools with a gradual sloped entry / shallow wading area. */
  sloped: boolean;
}

/** Read filters out of a route's query string. */
export function parseFilters(query: URLSearchParams): Filters {
  const indoorRaw = query.get("indoor");
  const indoor =
    indoorRaw === "indoor" || indoorRaw === "outdoor" ? indoorRaw : "all";
  return {
    openNow: query.get("open") === "1",
    indoor,
    sloped: query.get("sloped") === "1",
  };
}

/** Serialize filters back into a shareable hash for a pool tab (list/map). */
export function filtersToHash(filters: Filters, view: "list" | "map"): string {
  const q = new URLSearchParams();
  if (filters.openNow) q.set("open", "1");
  if (filters.indoor !== "all") q.set("indoor", filters.indoor);
  if (filters.sloped) q.set("sloped", "1");
  const qs = q.toString();
  if (view === "map") return qs ? `#/map?${qs}` : "#/map";
  return qs ? `#/?${qs}` : "#/";
}

/** Does any of a pool's sections read as open right now? */
export function poolIsOpenNow(status: PoolStatus | undefined): boolean {
  if (!status) return false;
  return status.sections.some((s) => effectiveStatus(s) === "open");
}

/** Open / total tallies for the citywide summary header. */
export interface SummaryCounts {
  /** Pools whose focus section (children's if present, else main) reads open right now. */
  open: number;
  /** Total pools in the set (the denominator) — always the FULL, unfiltered set. */
  total: number;
  /**
   * Count of pools currently visible after active filters narrowed the set.
   * Undefined when no filters are narrowing it (i.e. `shown` was omitted, or
   * equals `total`) — the summary line then omits the "· showing N" suffix.
   */
  showing?: number;
}

/**
 * Tally open / total pools across a (base, unfiltered) set, using whatever
 * statuses are available via `statusOf`. Pure — no DOM, no network — so the
 * summary header logic is unit-testable. Pools with no loaded status simply do
 * not count toward `open` (but still toward `total`).
 *
 * Counts each pool's *focus* section — the children's section when the pool has
 * one, otherwise the main pool — so main-only pools (now shown citywide) count
 * as open when their main section reads open, instead of only ever inflating the
 * denominator.
 *
 * The optional third argument, `shown`, is the currently-filtered subset of
 * `pools` (same population, e.g. both already exclude closed-for-season pools).
 * When its length differs from `pools.length`, `showing` is set so the caller
 * can render a stable "X of Y pools open now · showing N" line whose
 * denominator never moves as filters change — only pass `pools` for the old
 * (pre-Task-803ad825) two-arg behavior, which stays unchanged.
 */
export function summarizeOpen(
  pools: Pool[],
  statusOf: (poolId: string) => PoolStatus | undefined,
  shown?: Pool[],
): SummaryCounts {
  let open = 0;
  for (const pool of pools) {
    const status = statusOf(pool.poolId);
    const focus = pool.hasKiddiePool ? "kiddie" : "main";
    const section = status?.sections.find((s) => s.section === focus);
    if (section && effectiveStatus(section) === "open") open += 1;
  }
  const counts: SummaryCounts = { open, total: pools.length };
  if (shown !== undefined && shown.length !== pools.length) counts.showing = shown.length;
  return counts;
}

/**
 * Fold a pool/pad name or a search query into a comparable form: lower-cased,
 * diacritics stripped, and every run of punctuation/whitespace collapsed to a
 * single space. That makes "Ft. Stanton" match "ft stanton", "Fort Dupont"
 * match "fort  dupont", and the accented spellings people actually type match
 * the catalog's ASCII ones.
 */
export function normalizeForSearch(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/**
 * Does `name` match the free-text search `query`?
 *
 * Every whitespace-separated token of the query must appear somewhere in the
 * normalized name (substring, not word-prefix), so "ban rec" finds "Banneker
 * Recreation Center" and word order doesn't matter. An empty/whitespace-only
 * query matches everything — the caller can pass the raw input box value
 * without special-casing the initial state.
 *
 * Pure and allocation-cheap: the catalog is <100 names, so re-running this on
 * every (debounced) keystroke is free.
 */
export function matchesQuery(name: string, query: string): boolean {
  const tokens = normalizeForSearch(query).split(" ").filter(Boolean);
  if (!tokens.length) return true;
  const haystack = normalizeForSearch(name);
  return tokens.every((t) => haystack.includes(t));
}

/** Apply the static (status-independent) filters; open-now uses status separately. */
export function matchesStaticFilters(pool: Pool, filters: Filters): boolean {
  if (filters.indoor === "indoor" && pool.indoor !== true) return false;
  if (filters.indoor === "outdoor" && pool.indoor !== false) return false;
  if (filters.sloped && !pool.slopedEntry) return false;
  return true;
}
